import { useCallback, useEffect, useRef, useState } from 'react'

import {
  deleteBackground,
  listBackgrounds,
  uploadBackground,
  RequestTimeoutError,
  isOutcomeUnknown,
  type BackgroundLibraryKind,
  type LibraryBackground,
} from '../services/backgroundLibrary'

export interface UseBackgroundLibrary {
  backgrounds: LibraryBackground[]
  canManageWorkspace: boolean
  loading: boolean
  /**
   * The list could not be fetched. `backgrounds` and `canManageWorkspace` are
   * then unknown, not empty — surfaces show a load error with `reload` rather
   * than "not an admin" or "0 used".
   */
  loadFailed: boolean
  /** With `loadFailed`: why, in words that replace the surface's own load error. */
  loadError: string | null
  busy: boolean
  /** Last upload or delete failure, in words meant for the user. */
  error: string | null
  clearError: () => void
  upload: (file: File, scope: BackgroundLibraryKind) => Promise<LibraryBackground | null>
  remove: (id: string) => Promise<boolean>
  reload: () => void
}

/**
 * The user's own and their company's background images (SC-158).
 *
 * Loads once when `enabled` turns true — the picker is behind a dialog, so there
 * is no reason to fetch for someone who never opens it.
 *
 * Failures are surfaced rather than thrown: the picker still has blur and the
 * bundled presets to offer, so a library that will not load must not take the
 * whole control down — but it must say it did not load, not pretend to be empty.
 */
export function useBackgroundLibrary(enabled: boolean): UseBackgroundLibrary {
  const [backgrounds, setBackgrounds] = useState<LibraryBackground[]>([])
  const [canManageWorkspace, setCanManageWorkspace] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)
  // Whether a list has loaded at least once. A re-read that fails after that must not
  // replace a list the user can see with "could not load": the old one is still the
  // best account of what exists, and it keeps upload and the admin controls usable.
  const loadedOnce = useRef(false)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    // A re-read behind a visible list stays quiet instead of blanking it to a spinner.
    if (!loadedOnce.current) setLoading(true)
    setLoadFailed(false)
    setLoadError(null)

    void (async () => {
      try {
        const library = await listBackgrounds()
        if (cancelled) return
        if (library.loadFailed && loadedOnce.current) return
        if (!library.loadFailed) loadedOnce.current = true
        setBackgrounds(library.backgrounds)
        setCanManageWorkspace(library.canManageWorkspace)
        setLoadFailed(library.loadFailed)
        setLoadError(library.loadError ?? null)
      } catch (error) {
        // Defence in depth. `listBackgrounds` is total, but a spinner that never
        // stops is a bad way to find out that stopped being true.
        if (cancelled) return
        console.warn('[SC-158] Background library failed to load:', error)
        if (loadedOnce.current) return
        setBackgrounds([])
        setCanManageWorkspace(false)
        setLoadFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, reloadToken])

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])
  const clearError = useCallback(() => setError(null), [])

  const upload = useCallback(
    async (file: File, scope: BackgroundLibraryKind): Promise<LibraryBackground | null> => {
      setBusy(true)
      setError(null)
      try {
        const created = await uploadBackground({
          file,
          scope,
          // The filename is the only name we have to offer; the server trims and
          // sanitises it.
          // By code point: `slice` counts UTF-16 units and would cut an emoji at
          // the limit in half, leaving a lone surrogate.
          name: Array.from(file.name.replace(/\.[^.]+$/, '')).slice(0, 80).join(''),
        })
        setBackgrounds((current) => [created, ...current])
        return created
      } catch (uploadError: any) {
        setError(uploadError?.message ?? 'Could not save that background.')
        // A stalled request may have landed, and the server can answer 500 for a row
        // that is kept (its save rejected after the file went in), so the list is
        // re-read or the new image is missing and a retry duplicates it. Not for a
        // refusal or a request that never left the browser: nothing changed, and the
        // list call would only fail the same way.
        if (isOutcomeUnknown(uploadError)) reload()
        return null
      } finally {
        setBusy(false)
      }
    },
    [reload]
  )

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      await deleteBackground(id)
      setBackgrounds((current) => current.filter((background) => background.id !== id))
      return true
    } catch (deleteError: any) {
      setError(deleteError?.message ?? 'Could not remove that background.')
      // The delete may have gone through with the answer lost: re-read the list.
      if (deleteError instanceof RequestTimeoutError) reload()
      return false
    } finally {
      setBusy(false)
    }
  }, [reload])

  return {
    backgrounds,
    canManageWorkspace,
    loading,
    loadFailed,
    loadError,
    busy,
    error,
    clearError,
    upload,
    remove,
    reload,
  }
}

export default useBackgroundLibrary
